// FollowUpsTable — the call list itself (docs/follow-ups-plan.md §3). PURE:
// props in, markup out. The page fetches and polls; this renders what it is
// handed, in the order it is handed (the server sorts: due by the longest
// wait, snoozed by the soonest return, done by the latest mark), and reports a
// snooze / unsnooze / done mark / put-back through its callbacks.
//
// Layout: a call sheet. Five columns — who to call (name, then phone and
// email under it), where they are moving, how long it has waited, what is
// waiting (one line to say it, then the facts under it), and what to do with
// it: mark it done ("Called, done" or "Not needed") or snooze it.
// Phone and email sit under the name rather than in their own columns so the
// row reads as one person at a glance and the table stays narrow enough to
// scan on a wide monitor.
//
// Two rules the markup keeps:
//   • Never an id in text. An item's key travels only in `key` / `data-key`
//     and the callbacks; the AppFolio link carries its id only in `href`
//     (docs/no-ids-in-ui.md, pages/noIdsRendered.test.ts).
//   • No door back into the builder. The tenant's name opens AppFolio in a new
//     tab; phone and email are tel: / mailto:. Nothing links to another page
//     of this app — people who can see this list may not be able to open those.
//     The one exception is a verifier-call row's "Open the call sheet", which is
//     only ever sent to viewers who may open the call sheet.
//
// RRA v2.1 (W3.14, Miles's Decision 5): a third kind of row, `verifier_call` — a
// scheduled front-line phone call to a person who can confirm an accommodation
// request. It shows the verifier's name and phone (never a link into AppFolio),
// "Verifier call · for {subject} · call {n} of 3", an empty unit cell, the usual
// waiting cell, and ONE action: "Open the call sheet" (the signed-in call sheet,
// in a new tab). No Snooze, no Done — reporting the call clears the row. It is the
// only link on this page that goes into the app, and only the viewers who may
// open the call sheet are sent these rows.
//
// WEL-181: a rental-reference row also carries a badge beside the tenant's name
// ("Approved" / "Tenant now", from `rv.applicationStage`; nothing for null) and a
// "Cancel reference" button in its last cell — in the Due, Snoozed and Done lists
// alike. The button only REPORTS the key (`onCancelReference`); the page owns the
// confirm, the request and the reload. Lease and verifier-call rows have neither.
//
// Only type imports from the client, so rendering this pulls in no API module.

import type { FollowUpDoneReason, FollowUpItem, SnoozeDays } from '@/lib/followUpsClient';
import {
  CANCEL_REFERENCE_TEXT,
  applicationStageBadge,
  canCancelReference,
} from '@/lib/followUpsReference';

export interface FollowUpsTableProps {
  /** Items to call about now. Rendered in the order given. */
  due: FollowUpItem[];
  /** Items someone has snoozed. Rendered in the order given. */
  snoozed: FollowUpItem[];
  /** Items someone marked done. Rendered in the order given. */
  done: FollowUpItem[];
  onSnooze: (key: string, days: SnoozeDays) => void;
  onUnsnooze: (key: string) => void;
  onDone: (key: string, reason: FollowUpDoneReason) => void;
  onPutBack: (key: string) => void;
  /** WEL-181: staff chose "Cancel reference" on a reference row; the page confirms and cancels. */
  onCancelReference: (key: string) => void;
  /** The item whose action is in flight; its controls are disabled. */
  busyKey?: string | null;
  /**
   * True while ANY action is in flight: every row's controls are
   * disabled, because each response replaces the whole list and two overlapping
   * responses would each undo the other's row until the next poll.
   */
  mutating?: boolean;
}

export const EMPTY_DUE_TEXT = 'Nothing to follow up on.';

const SNOOZE_CHOICES: ReadonlyArray<{ days: SnoozeDays; label: string }> = [
  { days: 1, label: '1 day' },
  { days: 3, label: '3 days' },
  { days: 7, label: '7 days' },
];

const DONE_CHOICES: ReadonlyArray<{ reason: FollowUpDoneReason; label: string }> = [
  { reason: 'called', label: 'Called, done' },
  { reason: 'not_needed', label: 'Not needed' },
];

/** The call cap the ladder allows (G2: three unreached calls); "call {n} of 3". */
export const VERIFIER_CALL_CAP = 3;
/** DRAFT: the one action of a verifier-call row. */
export const OPEN_CALL_SHEET_TEXT = 'Open the call sheet';

/** "Verifier call · for Jane Requester · call 2 of 3" (DRAFT). A call past the cap only
 *  happens because staff asked for it from the console (G2), so it reads "call 4" — never
 *  "4 of 3", and never "3 of 3", which would tell front line it is the last of the ladder. */
export function verifierCallHeadline(call: NonNullable<FollowUpItem['verifierCall']>): string {
  const n = Math.max(1, Math.floor(call.callNumber));
  const which = n > VERIFIER_CALL_CAP ? `call ${n}` : `call ${n} of ${VERIFIER_CALL_CAP}`;
  return `Verifier call · for ${call.subjectName.trim() || 'the requester'} · ${which}`;
}

/** "Called" / "Not needed" — what the Done list says about a mark. */
export function doneLabel(reason: FollowUpDoneReason | undefined): string {
  return reason === 'not_needed' ? 'Not needed' : 'Called';
}

// ---------------------------------------------------------------------------
// Words (exported for the tests)
// ---------------------------------------------------------------------------

/**
 * "26 hours" under two days, whole days from there ("3 days"). Every due item
 * has waited at least 24 hours, so a day-count under two would only ever say
 * "1 day" for anything between 24 and 47 hours — hours are the honest unit.
 */
export function waitingLabel(hours: number): string {
  const h = Number.isFinite(hours) ? Math.max(0, Math.floor(hours)) : 0;
  if (h < 48) return h === 1 ? '1 hour' : `${h} hours`;
  return `${Math.floor(h / 24)} days`;
}

/** "Sep 24" — the date as staff say it on the phone. Null when unparseable. */
export function shortDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** "Oct 2, 9:00 AM" — a snooze end, with its time, since a 1-day snooze set at
 *  2pm still hides the item from the next morning's 9:15 digest. */
export function shortDateTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** "Wed, Sep 24, 2026, 10:04 AM" — the hover text behind a short label. */
export function fullDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * A dialable `tel:` href for a number shown exactly as AppFolio wrote it. The
 * text is never reformatted; only the href is reduced to digits (and a leading
 * +), dropping an extension. Null when nothing dialable is left.
 */
export function telHref(phone: string): string | null {
  const main = phone.split(/\s*(?:ext\.?|x)\s*\d/i)[0] ?? phone;
  const digits = main.replace(/[^\d+]/g, '');
  return /\d/.test(digits) ? `tel:${digits}` : null;
}

/** "Lease unsigned by Jane Doe, John Roe" / "Renewal unsigned by …". */
export function leaseHeadline(lease: NonNullable<FollowUpItem['lease']>): string {
  const what = lease.isRenewal ? 'Renewal' : 'Lease';
  const names = lease.outstanding
    .map((s) => s.name.trim() || s.role.trim())
    .filter((n) => n.length > 0);
  return names.length > 0 ? `${what} unsigned by ${names.join(', ')}` : `${what} unsigned`;
}

/** "1 of 3 signed · reminder sent Sep 24". */
export function leaseDetail(lease: NonNullable<FollowUpItem['lease']>): string {
  const parts = [`${lease.signedCount} of ${lease.totalCount} signed`];
  const reminded = shortDate(lease.lastReminderAt);
  if (reminded) parts.push(`reminder sent ${reminded}`);
  return parts.join(' · ');
}

/** "Sent Sep 24 · opened Sep 25 · 1 reminder" (or "not opened"). */
export function referenceProgress(item: FollowUpItem): string {
  const rv = item.rv;
  const parts: string[] = [];
  const sent = shortDate(item.waitingSince);
  if (sent) parts.push(`Sent ${sent}`);
  const opened = shortDate(rv?.openedAt);
  parts.push(opened ? `opened ${opened}` : 'not opened');
  const n = rv?.reminderCount ?? 0;
  if (n > 0) parts.push(n === 1 ? '1 reminder' : `${n} reminders`);
  return parts.join(' · ');
}

/**
 * The applicants who listed this home, when they are not simply the person
 * being called (§1.4: the call goes to the group's primary even when the
 * reference is a co-applicant's). Empty when there is nothing to add.
 */
export function listedBy(item: FollowUpItem): string[] {
  const names = (item.rv?.applicantNames ?? []).map((n) => n.trim()).filter(Boolean);
  const primary = item.primary.name.trim().toLowerCase();
  const onlyThePrimary = names.every((n) => n.toLowerCase() === primary);
  return onlyThePrimary ? [] : names;
}

// ---------------------------------------------------------------------------
// Cells
// ---------------------------------------------------------------------------

const SUB: React.CSSProperties = {
  display: 'block',
  color: 'var(--fg-muted)',
  fontSize: 'var(--text-sm)',
  lineHeight: 1.45,
  marginTop: 2,
};

const LEAD: React.CSSProperties = { fontWeight: 'var(--weight-medium)' as never };

function PhoneLink({ phone }: { phone: string | null }) {
  const text = phone?.trim();
  if (!text) return null;
  const href = telHref(text);
  return href ? <a href={href}>{text}</a> : <>{text}</>;
}

function EmailLink({ email }: { email: string | null }) {
  const text = email?.trim();
  if (!text) return null;
  return <a href={`mailto:${text}`}>{text}</a>;
}

/** Who to call: the name (a door into AppFolio), then phone and email under it. */
function TenantCell({ item }: { item: FollowUpItem }) {
  const { primary } = item;
  if (item.kind === 'verifier_call') {
    // The verifier: their name and phone — no AppFolio door, no email line.
    const phone = primary.phone?.trim();
    return (
      <td>
        <span style={LEAD}>{primary.name.trim() || 'Verifier'}</span>
        <span style={{ ...SUB, whiteSpace: 'nowrap' }}>
          {phone ? <PhoneLink phone={phone} /> : 'no phone on file'}
        </span>
      </td>
    );
  }
  const name = primary.name.trim() || 'Primary tenant';
  const phone = primary.phone?.trim();
  const email = primary.email?.trim();
  // WEL-181: where the household stands, on reference rows only.
  const badge = item.kind === 'rv' ? applicationStageBadge(item.rv?.applicationStage) : null;
  return (
    <td>
      <span style={LEAD}>
        {primary.appfolioUrl ? (
          <a href={primary.appfolioUrl} target="_blank" rel="noopener noreferrer">
            {name}
          </a>
        ) : (
          name
        )}
      </span>
      {badge && (
        <span
          className="badge"
          data-size="sm"
          data-tone={badge.tone}
          data-stage={item.rv?.applicationStage ?? undefined}
          style={{ marginLeft: 'var(--space-2)' }}
        >
          {badge.label}
        </span>
      )}
      {primary.appfolioUrl && primary.appfolioTarget === 'unit' && (
        <span style={SUB}>opens the unit in AppFolio</span>
      )}
      <span style={{ ...SUB, whiteSpace: 'nowrap' }}>
        {phone ? <PhoneLink phone={phone} /> : 'no phone on file'}
      </span>
      <span style={SUB}>{email ? <EmailLink email={email} /> : 'no email on file'}</span>
    </td>
  );
}

function UnitCell({ item }: { item: FollowUpItem }) {
  // A verifier call has no unit — nothing about the request's housing is shown.
  if (item.kind === 'verifier_call') return <td />;
  const line =
    item.unit.name ?? item.property.name ?? item.unit.address ?? item.property.address ?? null;
  const address = item.unit.address ?? item.property.address ?? null;
  return (
    <td>
      <span style={LEAD}>{line ?? '—'}</span>
      {address && address !== line && <span style={SUB}>{address}</span>}
    </td>
  );
}

function WaitingCell({ item }: { item: FollowUpItem }) {
  const since = fullDate(item.waitingSince);
  const day = shortDate(item.waitingSince);
  return (
    <td style={{ whiteSpace: 'nowrap' }}>
      <time
        dateTime={item.waitingSince}
        title={since ? `Waiting since ${since}` : undefined}
        style={LEAD}
      >
        {waitingLabel(item.waitingHours)}
      </time>
      {day && <span style={SUB}>since {day}</span>}
    </td>
  );
}

function WhatCell({ item }: { item: FollowUpItem }) {
  if (item.kind === 'lease' && item.lease) {
    return (
      <td>
        <span style={LEAD}>{leaseHeadline(item.lease)}</span>
        <span style={SUB}>{leaseDetail(item.lease)}</span>
      </td>
    );
  }
  if (item.kind === 'verifier_call' && item.verifierCall) {
    return (
      <td>
        <span style={LEAD}>{verifierCallHeadline(item.verifierCall)}</span>
      </td>
    );
  }
  if (item.kind === 'rv' && item.rv) {
    const { landlord, priorAddress } = item.rv;
    const landlordName = landlord.name.trim();
    const phone = landlord.phone?.trim();
    const email = landlord.email?.trim();
    const by = listedBy(item);
    return (
      <td>
        <span style={LEAD}>Rental reference unanswered</span>
        <span style={SUB}>
          {landlordName
            ? `Landlord: ${landlordName}`
            : phone || email
              ? 'Landlord not named'
              : 'No landlord contact on file'}
          {phone && (
            <>
              {' · '}
              <PhoneLink phone={phone} />
            </>
          )}
          {email && (
            <>
              {' · '}
              <EmailLink email={email} />
            </>
          )}
        </span>
        {priorAddress && <span style={SUB}>Previous home: {priorAddress}</span>}
        {by.length > 0 && <span style={SUB}>Listed by {by.join(', ')}</span>}
        <span style={SUB}>{referenceProgress(item)}</span>
      </td>
    );
  }
  // A kind this build does not know yet: say what little is certain.
  return <td>Waiting on a reply</td>;
}

const MENU: React.CSSProperties = {
  width: 'auto',
  height: 'var(--ui-h-sm)',
  fontSize: 'var(--ui-text-sm)',
};

/**
 * WEL-181: "Cancel reference" — on a reference row only (null for anything else).
 * It reports the row's key; the page confirms, cancels and reloads. Disabled
 * while anything is in flight, like every other control on the row.
 */
function CancelReferenceButton({
  item,
  busy,
  onCancelReference,
}: {
  item: FollowUpItem;
  busy: boolean;
  onCancelReference: FollowUpsTableProps['onCancelReference'];
}) {
  if (!canCancelReference(item)) return null;
  const who = item.primary.name.trim() || 'this follow-up';
  return (
    <div style={{ marginTop: 'var(--space-2)' }}>
      <button
        type="button"
        className="btn"
        data-variant="secondary"
        data-size="sm"
        aria-label={`${CANCEL_REFERENCE_TEXT} for ${who}`}
        disabled={busy}
        onClick={() => onCancelReference(item.key)}
      >
        {busy ? 'Working…' : CANCEL_REFERENCE_TEXT}
      </button>
    </div>
  );
}

/** A due row's actions: mark it done (the call is made, or none is needed), or snooze it. */
function ActionsCell({
  item,
  busy,
  onSnooze,
  onDone,
  onCancelReference,
}: {
  item: FollowUpItem;
  busy: boolean;
  onSnooze: FollowUpsTableProps['onSnooze'];
  onDone: FollowUpsTableProps['onDone'];
  onCancelReference: FollowUpsTableProps['onCancelReference'];
}) {
  // A scheduled verifier call is cleared by reporting the call on the call sheet:
  // its one action is the link — no Done menu, no Snooze menu.
  if (item.kind === 'verifier_call') {
    const url = item.verifierCall?.callSheetUrl;
    return (
      <td style={{ whiteSpace: 'nowrap' }}>
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn"
            data-variant="secondary"
            data-size="sm"
          >
            {OPEN_CALL_SHEET_TEXT}
          </a>
        ) : (
          '—'
        )}
      </td>
    );
  }
  const who = item.primary.name.trim() || 'this follow-up';
  return (
    <td style={{ whiteSpace: 'nowrap' }}>
      {/* Native selects, not popover menus: the table wrap scrolls
          sideways, which would clip an absolutely-positioned menu on the
          bottom rows; the browser draws a select's list above everything. */}
      <select
        className="input"
        aria-label={`Mark ${who} done`}
        value=""
        disabled={busy}
        onChange={(e) => {
          const choice = DONE_CHOICES.find((c) => c.reason === e.target.value);
          if (choice) onDone(item.key, choice.reason);
        }}
        style={{ ...MENU, display: 'block' }}
      >
        <option value="" disabled>
          {busy ? 'Working…' : 'Done…'}
        </option>
        {DONE_CHOICES.map((c) => (
          <option key={c.reason} value={c.reason}>
            {c.label}
          </option>
        ))}
      </select>
      <select
        className="input"
        aria-label={`Snooze ${who}`}
        value=""
        disabled={busy}
        onChange={(e) => {
          const choice = SNOOZE_CHOICES.find((c) => String(c.days) === e.target.value);
          if (choice) onSnooze(item.key, choice.days);
        }}
        style={{ ...MENU, display: 'block', marginTop: 'var(--space-2)' }}
      >
        <option value="" disabled>
          {busy ? 'Working…' : 'Snooze…'}
        </option>
        {SNOOZE_CHOICES.map((c) => (
          <option key={c.days} value={String(c.days)}>
            {c.label}
          </option>
        ))}
      </select>
      <CancelReferenceButton item={item} busy={busy} onCancelReference={onCancelReference} />
    </td>
  );
}

function SnoozedCell({
  item,
  busy,
  onUnsnooze,
  onCancelReference,
}: {
  item: FollowUpItem;
  busy: boolean;
  onUnsnooze: FollowUpsTableProps['onUnsnooze'];
  onCancelReference: FollowUpsTableProps['onCancelReference'];
}) {
  const snooze = item.snooze;
  const until = shortDateTime(snooze?.until);
  const at = fullDate(snooze?.at);
  const by = snooze?.by.trim();
  return (
    <td style={{ whiteSpace: 'nowrap' }}>
      <span style={{ ...SUB, marginTop: 0 }} title={at ? `Snoozed ${at}` : undefined}>
        {until ? `until ${until}` : 'snoozed'}
        {by ? ` by ${by}` : ''}
      </span>
      <button
        type="button"
        className="btn"
        data-variant="secondary"
        data-size="sm"
        disabled={busy}
        onClick={() => onUnsnooze(item.key)}
        style={{ marginTop: 'var(--space-2)' }}
      >
        {busy ? 'Working…' : 'Unsnooze'}
      </button>
      <CancelReferenceButton item={item} busy={busy} onCancelReference={onCancelReference} />
    </td>
  );
}

/** A done row: why and who, when, when it comes back (for a call), and Put back. */
function DoneCell({
  item,
  busy,
  onPutBack,
  onCancelReference,
}: {
  item: FollowUpItem;
  busy: boolean;
  onPutBack: FollowUpsTableProps['onPutBack'];
  onCancelReference: FollowUpsTableProps['onCancelReference'];
}) {
  const done = item.done;
  const by = done?.by.trim();
  const at = shortDateTime(done?.at);
  const until = shortDateTime(done?.until);
  const still = item.kind === 'rv' ? 'unanswered' : 'unsigned';
  return (
    <td>
      <span style={{ ...LEAD, display: 'block' }} title={fullDate(done?.at) ?? undefined}>
        {doneLabel(done?.reason)}
        {by ? ` by ${by}` : ''}
      </span>
      {at && <span style={SUB}>{at}</span>}
      {done?.reason === 'called' && until && <span style={SUB}>Back {until} if still {still}</span>}
      <button
        type="button"
        className="btn"
        data-variant="secondary"
        data-size="sm"
        disabled={busy}
        onClick={() => onPutBack(item.key)}
        style={{ marginTop: 'var(--space-2)' }}
      >
        {busy ? 'Working…' : 'Put back'}
      </button>
      <CancelReferenceButton item={item} busy={busy} onCancelReference={onCancelReference} />
    </td>
  );
}

// ---------------------------------------------------------------------------
// Tables
// ---------------------------------------------------------------------------

type ListKind = 'due' | 'snoozed' | 'done';

const LAST_COLUMN: Record<ListKind, { title: string; width: string }> = {
  due: { title: 'Done or snooze', width: '13%' },
  snoozed: { title: 'Snoozed', width: '15%' },
  done: { title: 'Done', width: '17%' },
};

function ItemsTable({
  items,
  list,
  busyKey,
  mutating,
  onSnooze,
  onUnsnooze,
  onDone,
  onPutBack,
  onCancelReference,
}: {
  items: FollowUpItem[];
  list: ListKind;
  busyKey: string | null;
  mutating: boolean;
  onSnooze: FollowUpsTableProps['onSnooze'];
  onUnsnooze: FollowUpsTableProps['onUnsnooze'];
  onDone: FollowUpsTableProps['onDone'];
  onPutBack: FollowUpsTableProps['onPutBack'];
  onCancelReference: FollowUpsTableProps['onCancelReference'];
}) {
  return (
    <div className="oc-table-wrap">
      <table className="oc-table" style={{ tableLayout: 'fixed', width: '100%' }}>
        <colgroup>
          <col style={{ width: '22%' }} />
          <col style={{ width: '22%' }} />
          <col style={{ width: '9%' }} />
          <col />
          <col style={{ width: LAST_COLUMN[list].width }} />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">Tenant</th>
            <th scope="col">Unit</th>
            <th scope="col">Waiting</th>
            <th scope="col">What</th>
            <th scope="col">{LAST_COLUMN[list].title}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const busy = busyKey === item.key || mutating === true;
            return (
              <tr key={item.key} data-key={item.key} data-kind={item.kind} style={{ verticalAlign: 'top' }}>
                <TenantCell item={item} />
                <UnitCell item={item} />
                <WaitingCell item={item} />
                <WhatCell item={item} />
                {list === 'snoozed' ? (
                  <SnoozedCell
                    item={item}
                    busy={busy}
                    onUnsnooze={onUnsnooze}
                    onCancelReference={onCancelReference}
                  />
                ) : list === 'done' ? (
                  <DoneCell
                    item={item}
                    busy={busy}
                    onPutBack={onPutBack}
                    onCancelReference={onCancelReference}
                  />
                ) : (
                  <ActionsCell
                    item={item}
                    busy={busy}
                    onSnooze={onSnooze}
                    onDone={onDone}
                    onCancelReference={onCancelReference}
                  />
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SectionHead({ id, title, count }: { id: string; title: string; count: number }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 'var(--space-2)',
        marginBottom: 'var(--space-2)',
      }}
    >
      <h2 id={id} className="oc-kicker" style={{ margin: 0 }}>
        {title}
      </h2>
      <span className="oc-meta" style={{ fontVariantNumeric: 'var(--numerals-tabular)' }}>
        · {count}
      </span>
    </div>
  );
}

export function FollowUpsTable({
  due,
  snoozed,
  done,
  onSnooze,
  onUnsnooze,
  onDone,
  onPutBack,
  onCancelReference,
  busyKey = null,
  mutating = false,
}: FollowUpsTableProps): React.JSX.Element {
  const actions = { busyKey, mutating, onSnooze, onUnsnooze, onDone, onPutBack, onCancelReference };
  return (
    <>
      <section aria-labelledby="follow-ups-due" data-section="due">
        <SectionHead id="follow-ups-due" title="Due" count={due.length} />
        {due.length === 0 ? (
          <div className="oc-table-wrap">
            <div className="oc-empty">{EMPTY_DUE_TEXT}</div>
          </div>
        ) : (
          <ItemsTable items={due} list="due" {...actions} />
        )}
      </section>
      {/* Snoozed and Done collapse to their heading and count when there is
          nothing in them — an empty table there would only be noise under the
          real list. */}
      <section
        aria-labelledby="follow-ups-snoozed"
        data-section="snoozed"
        style={{ marginTop: 'var(--space-6)' }}
      >
        <SectionHead id="follow-ups-snoozed" title="Snoozed" count={snoozed.length} />
        {snoozed.length > 0 && <ItemsTable items={snoozed} list="snoozed" {...actions} />}
      </section>
      <section
        aria-labelledby="follow-ups-done"
        data-section="done"
        style={{ marginTop: 'var(--space-6)' }}
      >
        <SectionHead id="follow-ups-done" title="Done" count={done.length} />
        {done.length > 0 && <ItemsTable items={done} list="done" {...actions} />}
      </section>
    </>
  );
}
